import type { GameId, Mode, Outcome, ShineState } from './ids.js'

/**
 * Reward arithmetic. MASTERPLAN §8.2 (sources), §8.3 (level curve), §9 (minigame tiers) are
 * the source of truth; CONTRACTS §3 adds the rules. Nothing here is a setter: the fold derives
 * every amount, there is no grant event and review mode never changes an amount.
 */

export type Reward = { stars: number; parilti: number }

export const REWARDS = {
  /** MASTERPLAN §5.4 outcome table. `shine` is applied per session, stars and Parıltı per window. */
  verified: { stars: 3, parilti: 30, shine: 40 },
  partial: { stars: 1, parilti: 10, shine: 15 },
  tried: { stars: 0, parilti: 0, shine: 5 },
  nocam: { stars: 0, parilti: 0, shine: 10 },
  /** A second session in a window that is already verified, or one outside both windows. */
  extra: { stars: 0, parilti: 0, shine: 10 },
  tamGun: { stars: 2, parilti: 20 },
  lessonCompleted: { stars: 5, parilti: 20 },
  quizFirstTry: { stars: 2, parilti: 10 },
  quizLaterTry: { stars: 1, parilti: 10 },
  minigameTutorial: { stars: 0, parilti: 15 },
  dailyFirstOpen: { stars: 0, parilti: 5 },
  badgeUnlock: { stars: 0, parilti: 25 },
  /** W3-OVERHAUL §4.3. The recap pays once per completion on a 7 day cooldown. */
  recap: { stars: 3, parilti: 30 },
  /** Per görev task, once per (localDate, task). */
  gorevItem: { stars: 0, parilti: 10 },
  /** Günün üçlüsü: all three tasks of a date, once per localDate; it needs the brush task. */
  gorevUclu: { stars: 2, parilti: 20 },
  feed: { shine: 5 },
  windowMissed: { shine: -25 },
} as const

export const CAPS = {
  /** One verified brush per window, so two a day (MASTERPLAN §5.1). */
  verifiedBrushesPerDay: 2,
  rewardedMinigamePlaysPerDay: 3,
  feedsPerDay: 2,
  /** Minimum spacing between two verified brushes. Closer than this runs as ekstra. */
  minVerifiedSpacingMs: 4 * 60 * 60 * 1000,
  maxLevel: 30,
  /** CONTRACTS §4: the ritual is 120 s of accumulated active time. */
  ritualMs: 120_000,
  quadrantMs: 30_000,
  maxPauseMs: 60_000,
  /** CONTRACTS §4 repeat guard. */
  repeatGuardMs: 30 * 60 * 1000,
  calendarDays: 400,
  /** W3-OVERHAUL §2.4: days between two rewarded recap completions. */
  recapCooldownDays: 7,
  gorevPerDay: 3,
} as const

/** MASTERPLAN §5.4 thresholds. Mini needs less active time; face presence is the same. */
export const VERIFY = {
  activeSeconds: { mini: 75, core: 90 },
  facePresentSeconds: 84,
  partialActiveSeconds: 45,
} as const

export const SHINE = { min: 0, max: 100 } as const

export type BrushSample = {
  activeSeconds: number
  facePresentSeconds: number
  cameraUsed: boolean
}

/** MASTERPLAN §5.4. The only place an outcome is decided, client and server alike. */
export function outcomeFor(sample: BrushSample, mode: Mode): Outcome {
  if (!sample.cameraUsed) return 'nocam'
  if (
    sample.activeSeconds >= VERIFY.activeSeconds[mode] &&
    sample.facePresentSeconds >= VERIFY.facePresentSeconds
  ) {
    return 'verified'
  }
  if (sample.activeSeconds >= VERIFY.partialActiveSeconds) return 'partial'
  return 'tried'
}

/** MASTERPLAN §7.1 display bands. Nothing else changes with shine. */
export function shineState(shine: number): ShineState {
  if (shine >= 75) return 'parlak'
  if (shine >= 40) return 'normal'
  if (shine >= 15) return 'mat'
  return 'uzgun'
}

export function clampShine(shine: number): number {
  return Math.min(SHINE.max, Math.max(SHINE.min, shine))
}

/**
 * Cumulative stars needed to reach level n (MASTERPLAN §8.3): sum over k of 5k + 5, k = 1..n-1.
 * The table in the plan is documentation; this is the generator.
 */
export function starsForLevel(level: number): number {
  if (level <= 1) return 0
  const m = level - 1
  return (5 * m * (m + 1)) / 2 + 5 * m
}

export function levelFor(stars: number): number {
  let level = 1
  while (level < CAPS.maxLevel && stars >= starsForLevel(level + 1)) level++
  return level
}

/** Stars still needed for the next level. 0 once the max level is reached. */
export function starsToNext(stars: number): number {
  const level = levelFor(stars)
  if (level >= CAPS.maxLevel) return 0
  return starsForLevel(level + 1) - stars
}

/** The pet look for a level (MASTERPLAN §7.2). Cosmetic only. */
export function petStageFor(level: number): 'sut' | 'buyuk' | 'parlak' {
  if (level >= 20) return 'parlak'
  if (level >= 10) return 'buyuk'
  return 'sut'
}

export type Tier = 5 | 10 | 15

/** MASTERPLAN §9 per game score thresholds, low tier first. */
export const GAME_TIERS: Record<GameId, readonly [number, number]> = {
  sekerci: [150, 300],
  disdostu: [100, 160],
  dogruyanlis: [60, 100],
  /** W3-F balance run: a stroke every 1.2 s lands near 530, every 0.7 s near 1050. */
  firtina: [450, 900],
  nefes: [80, 150],
  /** G balance run: a swap every 2.5 s lands near 2000 in Core, every 1.2 s near 3300. */
  plak: [1800, 3200],
  /** G balance run: standing still pays about 1180 in Core; the teeth collected are the tier. */
  kosu: [1400, 1900],
}

/**
 * The score a görev game task asks for (W3-OVERHAUL §2.5): Core the tier 10 threshold, Mini
 * half of it rounded down to a ten, so the lowest tier is still reachable for a small child.
 */
export function gorevTargetFor(gameId: GameId, mode: Mode): number {
  const tier10 = GAME_TIERS[gameId][0]
  return mode === 'mini' ? Math.max(10, Math.floor(tier10 / 20) * 10) : tier10
}

export function tierFor(gameId: GameId, score: number): Tier {
  const thresholds = GAME_TIERS[gameId]
  if (score >= thresholds[1]) return 15
  if (score >= thresholds[0]) return 10
  return 5
}

/** MASTERPLAN §8.2 streak milestones, once each. */
export const STREAK_MILESTONES: readonly { days: number; stars: number; parilti: number }[] = [
  { days: 3, stars: 5, parilti: 50 },
  { days: 7, stars: 10, parilti: 100 },
  { days: 14, stars: 15, parilti: 150 },
  { days: 30, stars: 30, parilti: 300 },
  { days: 60, stars: 40, parilti: 400 },
  { days: 100, stars: 60, parilti: 600 },
]

/** MASTERPLAN §5.4: a Dondurucu at streak 7 and at 30, at most two held. */
export const FREEZE_MILESTONES: readonly number[] = [7, 30]
export const FREEZE_CAP = 2
